import { useState, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { makeAIApiCall } from "../ai-api-service";
import { Role, Candidate } from "../candidate-types";

export const useCandidateSearch = () => {
  const [processLog, setProcessLog] = useState<string>("Click the AI Candidate Search button to use AI to find candidates for all roles at once, or select a role in the table to search candidates for a single role.");
  const [analyzing, setAnalyzing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [currentProcessingId, setCurrentProcessingId] = useState<string | null>(null);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [currentRoleIndex, setCurrentRoleIndex] = useState<number | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const cleanPdfText = (text: string): string => {
    return text
      .replace(/%[A-Fa-f0-9]{2}/g, '')
      .replace(/\[(.*?)\]/g, '')
      .replace(/<<(.*?)>>/g, '')
      .replace(/\\n/g, '\n')
      .replace(/\\[rt]/g, '')
      .replace(/obj|endobj|stream|endstream/g, '')
      .replace(/\d+ \d+ obj/g, '')
      .replace(/\/\w+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const clearExistingCandidatesForRole = async (projectId: string, roleTitle: string, userId: string) => {
    console.log(`[CandidateSearch] Starting cleanup for role: ${roleTitle} in project: ${projectId}`);
    
    try {
      // First, get existing candidates to see what we're deleting
      const { data: existingCandidates, error: fetchError } = await supabase
        .from('candidates')
        .select('id, candidate_name, candidate_id, match_percentage, created_at')
        .eq('project_id', projectId)
        .eq('role_title', roleTitle)
        .eq('user_id', userId);

      if (fetchError) {
        console.error('[CandidateSearch] Error fetching existing candidates for cleanup:', fetchError);
        return false;
      }

      if (existingCandidates && existingCandidates.length > 0) {
        console.log(`[CandidateSearch] Found ${existingCandidates.length} existing candidates to remove:`, 
          existingCandidates.map(c => ({
            id: c.id,
            name: c.candidate_name,
            candidate_id: c.candidate_id,
            match_percentage: c.match_percentage,
            created_at: c.created_at
          }))
        );

        // Delete existing candidates
        const { error: deleteError } = await supabase
          .from('candidates')
          .delete()
          .eq('project_id', projectId)
          .eq('role_title', roleTitle)
          .eq('user_id', userId);

        if (deleteError) {
          console.error('[CandidateSearch] Error deleting existing candidates:', deleteError);
          return false;
        }

        // Verify deletion was successful
        const { data: remainingCandidates, error: verifyError } = await supabase
          .from('candidates')
          .select('id, candidate_name')
          .eq('project_id', projectId)
          .eq('role_title', roleTitle)
          .eq('user_id', userId);

        if (verifyError) {
          console.error('[CandidateSearch] Error verifying candidate deletion:', verifyError);
          return false;
        }

        if (remainingCandidates && remainingCandidates.length > 0) {
          console.error('[CandidateSearch] Warning: Some candidates were not deleted:', remainingCandidates);
          return false;
        }

        console.log(`[CandidateSearch] Successfully deleted ${existingCandidates.length} existing candidates for role: ${roleTitle}`);
      } else {
        console.log(`[CandidateSearch] No existing candidates found for role: ${roleTitle}`);
      }

      return true;
    } catch (error) {
      console.error('[CandidateSearch] Unexpected error during candidate cleanup:', error);
      return false;
    }
  };

  const saveCandidatesWithIntegrityCheck = async (
    candidates: Candidate[], 
    selectedRole: Role, 
    userId: string,
    setProcessLog: (log: string | ((prev: string) => string)) => void
  ) => {
    const savedCandidateIds = [];
    const maxCandidates = Math.min(candidates.length, 3);

    console.log(`[CandidateSearch] Starting to save ${maxCandidates} candidates for role: ${selectedRole.role_title}`);

    for (let i = 0; i < maxCandidates; i++) {
      const candidate = candidates[i];
      const candidateId = i + 1;

      const candidateData = {
        project_id: selectedRole.project_id,
        project_name: selectedRole.name,
        role_title: selectedRole.role_title,
        role_description: selectedRole.role_description,
        candidate_name: candidate.name,
        linkedin_url: candidate.linkedinUrl,
        open_to_work: candidate.openToWork,
        match_percentage: String(candidate.matchPercentage),
        summary: candidate.summary,
        differentiators: candidate.differentiators,
        user_id: userId,
        candidate_id: candidateId,
        number_of_candidates: maxCandidates,
        profile_picture_url: candidate.profilePictureUrl
      };

      try {
        // Check if a candidate with this candidate_id already exists (should not happen after cleanup)
        const { data: existingCandidate, error: checkError } = await supabase
          .from('candidates')
          .select('id, candidate_name')
          .eq('project_id', selectedRole.project_id)
          .eq('role_title', selectedRole.role_title)
          .eq('candidate_id', candidateId)
          .eq('user_id', userId)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error(`[CandidateSearch] Error checking for existing candidate ${candidateId}:`, checkError);
          setProcessLog(prev => `${prev}\nError checking for duplicate candidate ${candidateId}: ${checkError.message}`);
          continue;
        }

        if (existingCandidate) {
          console.error(`[CandidateSearch] Warning: Candidate with ID ${candidateId} already exists:`, existingCandidate);
          setProcessLog(prev => `${prev}\nWarning: Candidate with ID ${candidateId} already exists (${existingCandidate.candidate_name})`);
          continue;
        }

        const { data: insertedCandidate, error: insertError } = await supabase
          .from('candidates')
          .insert(candidateData)
          .select('id')
          .maybeSingle();

        if (insertError) {
          console.error(`[CandidateSearch] Error inserting candidate ${candidateId}:`, insertError);
          setProcessLog(prev => `${prev}\nError saving candidate ${candidate.name}: ${insertError.message}`);
        } else if (insertedCandidate) {
          savedCandidateIds.push(insertedCandidate.id);
          console.log(`[CandidateSearch] Successfully saved candidate ${candidateId}: ${candidate.name} (ID: ${insertedCandidate.id})`);
          setProcessLog(prev => `${prev}\nSaved candidate ${candidateId}: ${candidate.name} (${candidate.candidateType || 'unknown'}, ${candidate.matchPercentage}%)`);
        }
      } catch (saveError) {
        console.error(`[CandidateSearch] Unexpected error saving candidate ${candidateId}:`, saveError);
        setProcessLog(prev => `${prev}\nUnexpected error saving ${candidate.name}: ${(saveError as Error).message}`);
      }
    }

    return savedCandidateIds;
  };

  const updateRoleJdReferences = async (
    savedCandidateIds: string[],
    selectedRole: Role,
    setProcessLog: (log: string | ((prev: string) => string)) => void
  ) => {
    if (savedCandidateIds.length === 0) return;

    console.log(`[CandidateSearch] Updating role_jd references for role: ${selectedRole.role_title}`);

    const updateData: any = {
      number_of_candidates: savedCandidateIds.length
    };

    if (savedCandidateIds[0]) updateData.candidate_id_1 = savedCandidateIds[0];
    if (savedCandidateIds[1]) updateData.candidate_id_2 = savedCandidateIds[1];
    if (savedCandidateIds[2]) updateData.candidate_id_3 = savedCandidateIds[2];

    try {
      const { error: roleUpdateError } = await supabase
        .from('role_jd')
        .update(updateData)
        .eq('project_id', selectedRole.project_id)
        .eq('role_title', selectedRole.role_title);

      if (roleUpdateError) {
        console.error('[CandidateSearch] Error updating role_jd with candidate IDs:', roleUpdateError);
        setProcessLog(prev => `${prev}\nWarning: Could not update role references: ${roleUpdateError.message}`);
      } else {
        console.log('[CandidateSearch] Successfully updated role_jd references');
      }
    } catch (error) {
      console.error('[CandidateSearch] Unexpected error updating role_jd:', error);
      setProcessLog(prev => `${prev}\nUnexpected error updating role references: ${(error as Error).message}`);
    }
  };

  const handleRowSelect = async (
    rowId: string,
    roles: Role[],
    selectedRowId: string | null,
    useRoleTitle: boolean,
    useRoleLocation: boolean,
    useOpenToWork: boolean,
    aiEngine: string,
    setCandidates: (candidates: Candidate[]) => void,
    setCandidatesPerRole: React.Dispatch<React.SetStateAction<{ [roleId: string]: Candidate[]; }>>,
    setSelectedRowId: (id: string | null) => void
  ) => {
    if (selectedRowId === rowId) {
      return;
    }

    setSelectedRowId(rowId);
    setCurrentProcessingId(rowId);
    setProcessLog("Starting AI candidate search process...");

    const selectedRole = roles.find(r => r.id === rowId);
    if (!selectedRole) {
      toast.error("Selected role not found");
      return;
    }

    const hasValidJD = selectedRole.role_description?.pdf_url || 
                      (selectedRole.role_description?.text && 
                       selectedRole.role_description.text.trim() !== '' && 
                       selectedRole.role_description.text !== 'No JD found');

    if (!hasValidJD) {
      toast.error("No job description found for this role");
      setProcessLog("Error: No job description found for this role");
      return;
    }

    try {
      setSearching(true);
      setAnalyzing(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Clear existing candidates for this role with enhanced verification
      setProcessLog(prev => `${prev}\nClearing existing candidates for role: ${selectedRole.role_title}...`);
      const cleanupSuccess = await clearExistingCandidatesForRole(
        selectedRole.project_id,
        selectedRole.role_title,
        user.id
      );

      if (!cleanupSuccess) {
        throw new Error("Failed to clear existing candidates. Aborting to prevent data inconsistency.");
      }

      setProcessLog(prev => `${prev}\nSuccessfully cleared existing candidates. Starting AI analysis using ${aiEngine.toUpperCase()}...`);

      // Extract structured location: country and city separately
      const locationMessages = [
        {
          role: 'system',
          content: `You are a parser that extracts location information from job descriptions. Return ONLY a JSON object with two fields: "country" (the country name) and "city" (the city or region, or empty string if not specified). Example: {"country": "Singapore", "city": "Singapore"} or {"country": "United States", "city": "New York"}. Return ONLY the JSON, no explanation.`
        },
        {
          role: 'user',
          content: selectedRole.role_description.text
        }
      ];

      const locationResponse = await makeAIApiCall('location', locationMessages, 'gpt-5.4-nano', aiEngine);
      let role_location = "";
      let target_country = "";
      let target_city = "";

      if (locationResponse.ok) {
        const locationData = await locationResponse.json();
        const locText = locationData.choices[0].message.content.trim();
        try {
          const locParsed = JSON.parse(locText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim());
          target_country = locParsed.country || '';
          target_city = locParsed.city || '';
          role_location = target_city && target_city !== target_country 
            ? `${target_city}, ${target_country}` 
            : target_country;
        } catch {
          // Fallback: treat entire response as location text
          role_location = locText;
          target_country = locText;
        }
      }

      setProcessLog(prev => `${prev}\nExtracted location: ${role_location} (Country: ${target_country}, City: ${target_city})`);

      // Extract industry, title synonyms, adjacent titles, and location synonyms from JD
      const extractionMessages = [
        {
          role: 'system',
          content: `You are a recruitment search optimizer. Given a job description, extract structured search criteria to find candidates on LinkedIn.

Return ONLY a JSON object with these fields:
- "industry": string (primary industry/sector, e.g., "Building and Construction", "Software Engineering")
- "keywords": array of 6-10 industry terms/synonyms that would appear on LinkedIn profiles (e.g., for construction: ["architecture", "built environment", "BCA", "M&E", "MEP", "HVAC", "contractor", "main contractor", "consultant", "real estate development"])
- "exclusions": array of 4-8 terms for commonly confused industries (e.g., for construction architect: ["software architect", "solution architect", "cloud architect", "enterprise architect", "data architect", "DevOps", "backend", "microservices"])
- "titleSynonyms": array of 3-5 equivalent job titles (same seniority, same function). Example for "Lead Architect": ["Principal Architect", "Senior Architect", "Project Architect", "Studio Director"]
- "adjacentTitles": array of 3-5 related but different-level titles. Example for "Lead Architect": ["Design Manager", "Architectural Lead", "Associate Architect", "Design Director", "Head of Architecture"]
- "locationSynonyms": array of 2-4 location terms for the target area. Example for Singapore: ["Singapore", "Greater Singapore Area", "SG"]

Return ONLY the JSON, no explanation.`
        },
        {
          role: 'user',
          content: `Role Title: ${selectedRole.role_title}\nLocation: ${role_location}\nCountry: ${target_country}\nCity: ${target_city}\n\nJob Description:\n${selectedRole.role_description.text}`
        }
      ];

      const extractionResponse = await makeAIApiCall('extraction', extractionMessages, 'gpt-5.4-nano', aiEngine);
      let role_industry = "";
      let industryKeywords: string[] = [];
      let industryExclusions: string[] = [];
      let titleSynonyms: string[] = [];
      let adjacentTitles: string[] = [];
      let locationSynonyms: string[] = [];

      if (extractionResponse.ok) {
        const extractionData = await extractionResponse.json();
        const extText = extractionData.choices[0].message.content.trim();
        try {
          const parsed = JSON.parse(extText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim());
          role_industry = parsed.industry || '';
          industryKeywords = parsed.keywords || [];
          industryExclusions = parsed.exclusions || [];
          titleSynonyms = parsed.titleSynonyms || [];
          adjacentTitles = parsed.adjacentTitles || [];
          locationSynonyms = parsed.locationSynonyms || [];
        } catch {
          role_industry = extText;
        }
      }

      setProcessLog(prev => `${prev}\nExtracted industry: ${role_industry || 'Not specified'}`);
      if (titleSynonyms.length > 0) {
        setProcessLog(prev => `${prev}\nTitle synonyms: ${titleSynonyms.join(', ')}`);
      }
      if (adjacentTitles.length > 0) {
        setProcessLog(prev => `${prev}\nAdjacent titles: ${adjacentTitles.join(', ')}`);
      }
      if (industryKeywords.length > 0) {
        setProcessLog(prev => `${prev}\nIndustry keywords: ${industryKeywords.join(', ')}`);
      }
      if (industryExclusions.length > 0) {
        setProcessLog(prev => `${prev}\nIndustry exclusions: ${industryExclusions.join(', ')}`);
      }

      setProcessLog(prev => `${prev}\n\nStarting external candidate search on LinkedIn via Google Custom Search...\n`);

      // Use Google Custom Search powered edge function with enriched parameters
      const { data: searchData, error: searchError } = await supabase.functions.invoke('search-linkedin-candidates', {
        body: {
          roleTitle: selectedRole.role_title,
          roleLocation: role_location,
          roleIndustry: role_industry,
          jobDescription: selectedRole.role_description.text,
          useRoleLocation,
          numberOfResults: 15,
          targetCountry: target_country,
          targetCity: target_city,
          industryKeywords,
          industryExclusions,
          titleSynonyms,
          adjacentTitles,
          locationSynonyms,
        }
      });

      if (searchError) {
        console.error('[CandidateSearch] Google Custom Search error:', searchError);
        throw new Error('Failed to search for LinkedIn candidates via Google Custom Search');
      }

      const linkedInProfiles = searchData?.candidates || [];
      setProcessLog(prev => `${prev}\nGoogle Custom Search found ${linkedInProfiles.length} LinkedIn candidates for "${selectedRole.role_title}"${role_location ? ` in ${role_location}` : ''}`);

      // Fetch internal candidates from database - ONLY those with matching role
      setProcessLog(prev => `${prev}\nFetching internal candidates with role matching "${selectedRole.role_title}"...`);
      
      const { data: internalCandidates, error: internalError } = await supabase
        .from('internal_candidates')
        .select('*')
        .eq('project_id', selectedRole.project_id)
        .eq('role', selectedRole.role_title);

      if (internalError) {
        console.error('Error fetching internal candidates:', internalError);
        setProcessLog(prev => `${prev}\nWarning: Could not fetch internal candidates`);
      }

      const internalCandidatesList = internalCandidates || [];
      setProcessLog(prev => `${prev}\nFound ${internalCandidatesList.length} internal candidates with matching role "${selectedRole.role_title}"`);

      // --- Deterministic location helpers ---
      const normalizeLocationText = (text: string): string => {
        return text.toLowerCase().replace(/[^a-z0-9\s,]/g, '').trim();
      };

      const extractCandidateLocation = (text: string): string => {
        return text;
      };

      const locationAliases: Record<string, string[]> = {
        'thailand': ['bangkok', 'thai', 'chiang mai', 'phuket', 'pattaya'],
        'singapore': ['sg', 'singaporean'],
        'united states': ['usa', 'us', 'america', 'american'],
        'united kingdom': ['uk', 'britain', 'british', 'england', 'london', 'scotland', 'wales'],
        'india': ['indian', 'mumbai', 'delhi', 'bangalore', 'bengaluru', 'hyderabad', 'chennai'],
        'australia': ['australian', 'sydney', 'melbourne', 'brisbane', 'perth'],
        'japan': ['japanese', 'tokyo', 'osaka'],
        'china': ['chinese', 'beijing', 'shanghai', 'shenzhen', 'guangzhou'],
        'germany': ['german', 'berlin', 'munich', 'frankfurt', 'hamburg'],
        'france': ['french', 'paris', 'lyon', 'marseille'],
        'canada': ['canadian', 'toronto', 'vancouver', 'montreal', 'ottawa'],
        'uae': ['dubai', 'abu dhabi', 'emirates'],
        'malaysia': ['malaysian', 'kuala lumpur', 'kl'],
        'indonesia': ['indonesian', 'jakarta'],
        'philippines': ['filipino', 'manila', 'cebu'],
        'vietnam': ['vietnamese', 'hanoi', 'ho chi minh'],
        'south korea': ['korean', 'seoul'],
        'hong kong': ['hk'],
        'new zealand': ['auckland', 'wellington', 'kiwi'],
        'brazil': ['brazilian', 'sao paulo', 'rio'],
        'mexico': ['mexican', 'mexico city'],
        'taiwan': ['taiwanese', 'taipei'],
        'saudi arabia': ['riyadh', 'jeddah', 'saudi'],
        'qatar': ['doha', 'qatari'],
        'south africa': ['johannesburg', 'cape town', 'south african'],
        'nigeria': ['nigerian', 'lagos', 'abuja'],
        'kenya': ['kenyan', 'nairobi'],
        'egypt': ['egyptian', 'cairo'],
      };

      // --- Regional proximity helpers ---
      const regionMap: Record<string, string> = {
        'thailand': 'southeast_asia', 'singapore': 'southeast_asia', 'malaysia': 'southeast_asia',
        'indonesia': 'southeast_asia', 'philippines': 'southeast_asia', 'vietnam': 'southeast_asia',
        'cambodia': 'southeast_asia', 'myanmar': 'southeast_asia', 'laos': 'southeast_asia',
        'brunei': 'southeast_asia',
        'china': 'east_asia', 'japan': 'east_asia', 'south korea': 'east_asia',
        'taiwan': 'east_asia', 'hong kong': 'east_asia',
        'india': 'south_asia', 'pakistan': 'south_asia', 'bangladesh': 'south_asia',
        'sri lanka': 'south_asia', 'nepal': 'south_asia',
        'australia': 'oceania', 'new zealand': 'oceania',
        'united states': 'north_america', 'canada': 'north_america',
        'mexico': 'latin_america', 'brazil': 'latin_america', 'argentina': 'latin_america',
        'colombia': 'latin_america', 'chile': 'latin_america', 'peru': 'latin_america',
        'united kingdom': 'europe', 'germany': 'europe', 'france': 'europe',
        'netherlands': 'europe', 'spain': 'europe', 'italy': 'europe',
        'sweden': 'europe', 'norway': 'europe', 'denmark': 'europe',
        'switzerland': 'europe', 'belgium': 'europe', 'austria': 'europe',
        'poland': 'europe', 'ireland': 'europe', 'portugal': 'europe',
        'uae': 'middle_east', 'saudi arabia': 'middle_east', 'qatar': 'middle_east',
        'bahrain': 'middle_east', 'kuwait': 'middle_east', 'oman': 'middle_east',
        'israel': 'middle_east', 'turkey': 'middle_east',
        'south africa': 'africa', 'nigeria': 'africa', 'kenya': 'africa',
        'egypt': 'africa', 'ghana': 'africa', 'tanzania': 'africa',
      };

      const adjacentRegions: Record<string, string[]> = {
        'southeast_asia': ['east_asia', 'south_asia', 'oceania'],
        'east_asia': ['southeast_asia', 'south_asia'],
        'south_asia': ['southeast_asia', 'east_asia', 'middle_east'],
        'oceania': ['southeast_asia', 'east_asia'],
        'north_america': ['latin_america', 'europe'],
        'latin_america': ['north_america'],
        'europe': ['middle_east', 'africa', 'north_america'],
        'middle_east': ['south_asia', 'europe', 'africa'],
        'africa': ['europe', 'middle_east'],
      };

      const getCountryFromText = (text: string): string | null => {
        const normalized = normalizeLocationText(text);
        for (const [country, aliases] of Object.entries(locationAliases)) {
          const allTerms = [country, ...aliases];
          if (allTerms.some(term => normalized.includes(term))) return country;
        }
        for (const country of Object.keys(regionMap)) {
          if (normalized.includes(country)) return country;
        }
        return null;
      };

      const getRegion = (locationText: string): string | null => {
        const country = getCountryFromText(locationText);
        if (country && regionMap[country]) return regionMap[country];
        return null;
      };

      const getProximityBonus = (candidateLocText: string, roleLocText: string): number => {
        const candidateRegion = getRegion(candidateLocText);
        const roleRegion = getRegion(roleLocText);
        if (!candidateRegion || !roleRegion) return 0;
        if (candidateRegion === roleRegion) return 15;
        if (adjacentRegions[roleRegion]?.includes(candidateRegion)) return 5;
        return 0;
      };

      const isLocationMismatch = (candidateLocationText: string, requiredLocation: string): boolean => {
        if (!requiredLocation || !candidateLocationText) return false;
        
        const reqLoc = normalizeLocationText(requiredLocation);
        const candLoc = normalizeLocationText(candidateLocationText);
        
        if (!candLoc) return false;
        
        const reqKeywords = reqLoc.split(/[\s,]+/).filter(w => w.length > 2);
        const hasMatch = reqKeywords.some(keyword => candLoc.includes(keyword));
        if (hasMatch) return false;
        
        for (const [country, aliases] of Object.entries(locationAliases)) {
          const allTerms = [country, ...aliases];
          const reqMatches = allTerms.some(term => reqLoc.includes(term));
          if (reqMatches) {
            const candMatches = allTerms.some(term => candLoc.includes(term));
            if (candMatches) return false;
            return true;
          }
        }
        
        const knownCountries = ['united states', 'usa', 'america', 'california', 'new york', 'texas', 'florida',
          'maryland', 'virginia', 'illinois', 'ohio', 'georgia', 'washington', 'colorado', 'arizona',
          'united kingdom', 'uk', 'england', 'london', 'india', 'australia', 'canada', 'germany', 'france',
          'japan', 'china', 'brazil', 'mexico', 'south korea', 'indonesia', 'philippines', 'vietnam',
          'thailand', 'singapore', 'malaysia', 'hong kong', 'uae', 'dubai', 'new zealand',
          'oakland', 'san francisco', 'los angeles', 'chicago', 'houston', 'seattle', 'boston', 'denver',
          'toronto', 'vancouver', 'sydney', 'melbourne', 'mumbai', 'delhi', 'bangalore', 'berlin', 'paris'];
        
        const candidateCountry = knownCountries.find(c => candLoc.includes(c));
        if (candidateCountry) {
          const reqMatchesCandidate = reqKeywords.some(k => candidateCountry.includes(k)) || 
                                      normalizeLocationText(candidateLocationText).includes(reqLoc);
          if (!reqMatchesCandidate) return true;
        }
        
        return false;
      };
      // --- End location helpers ---

      const processedCandidates: Array<any & { locationMatch: boolean }> = [];

      // Process external candidates from Perplexity results
      for (const profile of linkedInProfiles) {
        const profileUrl = profile.linkedinUrl;
        const candidateName = profile.name || 'Unknown';
        const candidateLocationText = profile.location || '';
        const candidateTitle = profile.currentTitle || '';
        const candidateCompany = profile.currentCompany || '';
        const candidateIndustry = profile.industry || '';
        const candidateSummary = profile.summary || '';
        const candidateYears = profile.yearsExperience || 0;

        // Deterministic location pre-filtering
        const locationMismatch = useRoleLocation && role_location && isLocationMismatch(candidateLocationText, role_location);

        if (locationMismatch) {
          console.log(`[CandidateSearch] SKIPPING external candidate "${candidateName}" - location mismatch. Required: "${role_location}", Found: "${candidateLocationText}"`);
          setProcessLog(prev => `${prev}\n⛔ Skipped: ${candidateName} — not based in ${role_location} (located in ${candidateLocationText})`);
          continue;
        }

        const locationPromptRule = useRoleLocation
          ? `LOCATION RULE: The required location is "${role_location}". The candidate's current location is "${candidateLocationText}".
- If the candidate is NOT currently residing in or near "${role_location}", cap the match percentage at 60% maximum.
- Candidates IN the required location get a location bonus; those outside are capped at 60%.`
          : `LOCATION PREFERENCE: The role is based in "${role_location}". Location is NOT a hard requirement, but candidates in the same geographic region as "${role_location}" should be viewed more favorably.`;

        const locationScoreInstruction = useRoleLocation
          ? `1) Match percentage (60% max if location mismatch, 5% max if industry mismatch)`
          : `1) Match percentage (candidates in same region as "${role_location}" get a slight bonus)`;

        const matchAnalysisMessages = [
          {
            role: 'system',
            content: `You are a strict recruitment expert. You MUST follow these rules:

${locationPromptRule}

ABSOLUTE INDUSTRY RULE: The required industry is "${role_industry || 'as specified in the job description'}". 
- If the candidate works in a DIFFERENT industry, the match percentage MUST be 5% or lower. 
- "Lead Architect" in construction ≠ "Lead Architect" in software. "Project Director" in IT ≠ "Project Director" in real estate. Be precise.

Candidates who match the industry AND are in the required location can score up to 100%.`
          },
          {
            role: 'user',
            content: `Analyze this external candidate profile:\n\nName: ${candidateName}\nCurrent Title: ${candidateTitle}\nCompany: ${candidateCompany}\nLocation: ${candidateLocationText}\nIndustry: ${candidateIndustry}\nYears Experience: ${candidateYears}\nSummary: ${candidateSummary}\nProfile URL: ${profileUrl}\n\nRequired location: ${role_location}\nRequired industry: ${role_industry || 'as specified in job description'}\n\nJob requirements:\n${selectedRole.role_description.text}\n\nSTEP 1: State the candidate's CURRENT location/country of residence.\nSTEP 2: State whether they are in "${role_location}" — YES or NO.\nSTEP 3: State the candidate's industry.\nSTEP 4: State whether it matches "${role_industry}" — YES or NO.\nSTEP 5: Provide:\n${locationScoreInstruction}\n2) Brief summary\n3) Key candidate differentiators (as array)`
          }
        ];

        const matchAnalysisResponse = await makeAIApiCall('match-analysis', matchAnalysisMessages, 'gpt-5.4-nano', aiEngine);

        if (!matchAnalysisResponse.ok) {
          continue;
        }

        const matchData = await matchAnalysisResponse.json();
        const analysis = matchData.choices[0].message.content;

        try {
          const matchPercentageRegex = /(\d+)%/;
          const matchResult = analysis.match(matchPercentageRegex);
          let matchPercentage = matchResult ? parseInt(matchResult[1]) : 0;

          // Secondary safeguard: cap at 60% if location mismatch detected and checkbox is checked
          if (useRoleLocation && role_location && matchPercentage > 60) {
            const step2No = /step\s*2[:\s]*no/i.test(analysis) || /not\s+(currently\s+)?(based|located|residing)\s+in/i.test(analysis.toLowerCase());
            if (step2No) {
              console.log(`[CandidateSearch] AI said location mismatch but scored ${matchPercentage}%. Capping at 60%.`);
              matchPercentage = 60;
            }
          }

          // Apply regional proximity bonus when location checkbox is unchecked
          if (!useRoleLocation && role_location && candidateLocationText) {
            const bonus = getProximityBonus(candidateLocationText, role_location);
            if (bonus > 0) {
              console.log(`[CandidateSearch] Applying proximity bonus +${bonus}% to external candidate (near ${role_location})`);
              matchPercentage = Math.min(100, matchPercentage + bonus);
            }
          }

          let summary = analysis.split('2)')[1]?.split('3)')[0] || '';
          summary = summary.trim();
          // Fall back to Perplexity-provided summary if AI parsing yields empty
          if (!summary) summary = candidateSummary;

          const differentiatorsText = analysis.split(/Key differentiators|Differentiators/i)[1] || '';
          const differentiators = differentiatorsText
            .split(/[\n\r]/)
            .map(item => item.replace(/^[-•*]\s*/, '').trim())
            .filter(item => item.length > 0);

          const candidateObj = {
            id: processedCandidates.length + 1,
            name: candidateName,
            project_name: selectedRole.project_name,
            project_id: selectedRole.project_id,
            candidate_name: candidateName,
            roleTitle: selectedRole.role_title,
            pdfUrl: '',
            matchPercentage,
            summary,
            differentiators,
            linkedinUrl: profileUrl,
            openToWork: false,
            profilePictureUrl: null,
            candidateType: 'external' as const,
            locationMatch: !locationMismatch
          };

          processedCandidates.push(candidateObj);
          setProcessLog(prev => `${prev}\nExternal candidate fit (${aiEngine.toUpperCase()} analysis): ${candidateName} (Match: ${matchPercentage}%)`);
        } catch (parseError) {
          console.error('Error parsing external candidate AI response:', parseError);
          continue;
        }
      }

      // Process internal candidates - only those with matching role
      for (const internalCandidate of internalCandidatesList) {
        const candidateProfile = `Name: ${internalCandidate.candidate_name}\nRole: ${internalCandidate.role}\nCountry: ${internalCandidate.country}\nCity: ${internalCandidate.city}\nSkillset: ${internalCandidate.skillset}\nYears of Experience: ${internalCandidate.years_experience}\nPast Projects: ${internalCandidate.past_projects}`;

        // Check internal candidate location deterministically
        const internalLocationText = `${internalCandidate.city} ${internalCandidate.country}`;
        const internalLocationMismatch = useRoleLocation && role_location && isLocationMismatch(internalLocationText, role_location);

        const internalLocationPromptRule = useRoleLocation
          ? `LOCATION RULE: The required location is "${role_location}". The candidate's current location is "${internalCandidate.city}, ${internalCandidate.country}".
- If the candidate is NOT currently residing in or near "${role_location}", cap the match percentage at 75% maximum. This is an internal candidate whose track record is known, so experience and industry fit are more important than exact location.`
          : `LOCATION PREFERENCE: The role is based in "${role_location}". Location is NOT a hard requirement, but internal candidates in the same geographic region as "${role_location}" should be viewed more favorably. Internal candidates have a known track record and should generally score higher than external candidates.`;

        const internalLocationScoreInstruction = useRoleLocation
          ? `1) Match percentage (75% max if location mismatch, 5% max if industry mismatch — internal candidate track record is known)`
          : `1) Match percentage (internal candidates in same region as "${role_location}" get a notable bonus for proximity and known track record)`;

        const matchAnalysisMessages = [
          {
            role: 'system',
            content: `You are a strict recruitment expert. You MUST follow these rules:

${internalLocationPromptRule}

ABSOLUTE INDUSTRY RULE: The required industry is "${role_industry || 'as specified in the job description'}". 
- If the candidate works in a DIFFERENT industry, the match percentage MUST be 5% or lower.

Candidates who match the industry AND are in the required location can score up to 100%.`
          },
          {
            role: 'user',
            content: `Analyze this internal candidate profile:\n${candidateProfile}\n\nRequired location: ${role_location}\nRequired industry: ${role_industry || 'as specified in job description'}\n\nJob requirements:\n${selectedRole.role_description.text}\n\nSTEP 1: State the candidate's CURRENT location: "${internalCandidate.city}, ${internalCandidate.country}".\nSTEP 2: Is this "${role_location}"? YES or NO.\nSTEP 3: State the candidate's industry.\nSTEP 4: Does it match "${role_industry}"? YES or NO.\nSTEP 5: Provide:\n${internalLocationScoreInstruction}\n2) Brief summary\n3) Key candidate differentiators (as array)`
          }
        ];

        const matchAnalysisResponse = await makeAIApiCall('match-analysis', matchAnalysisMessages, 'gpt-5.4-nano', aiEngine);

        if (!matchAnalysisResponse.ok) {
          continue;
        }

        const matchData = await matchAnalysisResponse.json();
        const analysis = matchData.choices[0].message.content;

        try {
          const matchPercentageRegex = /(\d+)%/;
          const matchResult = analysis.match(matchPercentageRegex);
          let matchPercentage = matchResult ? parseInt(matchResult[1]) : 0;

          // Deterministic cap for internal candidates with wrong location (75% - more lenient than external 60%)
          if (internalLocationMismatch && matchPercentage > 75) {
            console.log(`[CandidateSearch] Internal candidate "${internalCandidate.candidate_name}" location mismatch (${internalCandidate.city}, ${internalCandidate.country} vs ${role_location}). Capping at 75%.`);
            matchPercentage = 75;
          }

          // Apply regional proximity bonus when location checkbox is unchecked (higher bonus for internal candidates)
          if (!useRoleLocation && role_location) {
            const bonus = getProximityBonus(internalLocationText, role_location);
            // Internal candidates get a higher proximity bonus: +20 same region, +10 adjacent (vs external +15/+5)
            const internalBonus = bonus > 0 ? bonus + 5 : 0;
            if (internalBonus > 0) {
              console.log(`[CandidateSearch] Applying internal proximity bonus +${internalBonus}% to "${internalCandidate.candidate_name}"`);
              matchPercentage = Math.min(100, matchPercentage + internalBonus);
            }
          }

          let summary = analysis.split('2)')[1]?.split('3)')[0] || '';
          summary = summary.trim();

          const differentiatorsText = analysis.split(/Key differentiators|Differentiators/i)[1] || '';
          const differentiators = differentiatorsText
            .split(/[\n\r]/)
            .map(item => item.replace(/^[-•*]\s*/, '').trim())
            .filter(item => item.length > 0);

          const candidateObj = {
            id: processedCandidates.length + 1,
            name: internalCandidate.candidate_name,
            project_name: selectedRole.project_name,
            project_id: selectedRole.project_id,
            candidate_name: internalCandidate.candidate_name,
            roleTitle: selectedRole.role_title,
            pdfUrl: '',
            matchPercentage,
            summary,
            differentiators,
            linkedinUrl: internalCandidate.linkedin_url,
            openToWork: false,
            profilePictureUrl: null,
            candidateType: 'internal' as const,
            locationMatch: !internalLocationMismatch
          };

          processedCandidates.push(candidateObj);
          setProcessLog(prev => `${prev}\nInternal candidate fit (${aiEngine.toUpperCase()} analysis): ${internalCandidate.candidate_name} (Match: ${matchPercentage}%)`);
        } catch (parseError) {
          console.error('Error parsing internal candidate AI response:', parseError);
          continue;
        }
      }

      // Sort: location-matching candidates first, then by match percentage
      processedCandidates.sort((a, b) => {
        // Location-matching candidates always rank above non-matching
        if (a.locationMatch && !b.locationMatch) return -1;
        if (!a.locationMatch && b.locationMatch) return 1;
        // Within same location-match group, sort by match percentage
        return b.matchPercentage - a.matchPercentage;
      });
      const topCandidates = processedCandidates.slice(0, 5);
      
      setProcessLog(prev => `${prev}\n\nSelected top 5 candidates from ${processedCandidates.length} total candidates (${linkedInProfiles.length} external + ${internalCandidatesList.length} internal with matching role)`);
      
      setCandidates(topCandidates);

      // Save candidates with enhanced integrity checks
      const savedCandidateIds = await saveCandidatesWithIntegrityCheck(
        topCandidates,
        selectedRole,
        user.id,
        setProcessLog
      );

      // Update role_jd table with saved candidate references
      await updateRoleJdReferences(savedCandidateIds, selectedRole, setProcessLog);

      setCandidatesPerRole(prev => ({
        ...prev,
        [rowId]: topCandidates
      }));

      setProcessLog(prev => `${prev}\nCandidate search completed successfully. Saved ${savedCandidateIds.length} candidates with data integrity checks.`);

    } catch (error) {
      console.error('Error in handleRowSelect:', error);
      toast.error((error as Error).message || "Failed to search for candidates");
      setProcessLog(prev => `${prev}\nError: ${(error as Error).message}`);
    } finally {
      setSearching(false);
      setAnalyzing(false);
      setCurrentProcessingId(null);
    }
  };

  const handleGenerateAll = async (
    roles: Role[],
    candidatesPerRole: { [roleId: string]: Candidate[]; },
    handleRowSelectFn: (roleId: string) => Promise<void>
  ) => {
    if (isGeneratingAll || !roles.length) return;

    setIsGeneratingAll(true);
    setProcessLog(`Starting automated candidate search for all roles using AI...`);

    for (let i = 0; i < roles.length; i++) {
      setCurrentRoleIndex(i);
      const role = roles[i];

      if (candidatesPerRole[role.id]?.length > 0) {
        setProcessLog(prev => `${prev}\nSkipping ${role.role_title} - already has candidates`);
        continue;
      }

      setProcessLog(prev => `${prev}\n\nProcessing role ${i + 1}/${roles.length}: ${role.role_title}`);

      try {
        await handleRowSelectFn(role.id);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Error processing role ${role.role_title}:`, error);
        setProcessLog(prev => `${prev}\nError processing ${role.role_title}: ${(error as Error).message}`);
      }
    }

    setIsGeneratingAll(false);
    setCurrentRoleIndex(null);
    setProcessLog(prev => `${prev}\n\nCompleted processing all roles!`);
  };

  return {
    processLog,
    setProcessLog,
    analyzing,
    searching,
    currentProcessingId,
    isGeneratingAll,
    currentRoleIndex,
    textAreaRef,
    cleanPdfText,
    handleRowSelect,
    handleGenerateAll
  };
};
